import { useCallback, useEffect, useState } from "react";
import { Download, Eye, FileText, Trash2, Upload, X, Loader2 } from "lucide-react";
import { api, API_URL } from "../lib/api";

const CATEGORIES = [
  { id: "rfc", label: "RFC" },
  { id: "csf", label: "Constancia de Situación Fiscal" },
  { id: "acta_constitutiva", label: "Acta Constitutiva" },
  { id: "comprobante_domicilio", label: "Comprobante de Domicilio" },
  { id: "caratula_bancaria", label: "Carátula Bancaria" },
  { id: "licencia", label: "Licencias / Permisos" },
  { id: "otro", label: "Otro documento" },
];

function bytes(size) {
  if (!size) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
}

export default function BusinessDocumentsPanel() {
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [preview, setPreview] = useState(null);

  const load = useCallback(() => {
    api.get("/business-profile/documents", { withCredentials: true })
      .then((r) => setDocuments(r.data || []))
      .catch(() => setDocuments([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async (file, category) => {
    if (!file) return;
    setUploading(true);
    setError("");
    const body = new FormData();
    body.append("file", file);
    body.append("category", category);
    body.append("label", file.name);
    try {
      await api.post("/business-profile/documents", body, { withCredentials: true });
      setNotice("Documento guardado.");
      setTimeout(() => setNotice(""), 2500);
      load();
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos subir el documento.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este documento? Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/business-profile/documents/${id}`, { withCredentials: true });
      load();
    } catch (_) { /* noop */ }
  };

  const rename = async (id, currentLabel) => {
    const label = window.prompt("Nuevo nombre para el documento", currentLabel);
    if (!label || label === currentLabel) return;
    try {
      await api.patch(`/business-profile/documents/${id}`, { label }, { withCredentials: true });
      load();
    } catch (_) { /* noop */ }
  };

  const openPreview = async (doc) => {
    const token = window.localStorage.getItem("session_token");
    try {
      const resp = await fetch(`${API_URL}/business-profile/documents/${doc.id}/preview`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error("preview");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      setPreview({ ...doc, url });
    } catch (_) {
      setError("No pudimos abrir la vista previa.");
    }
  };

  const download = async (doc) => {
    const token = window.localStorage.getItem("session_token");
    try {
      const resp = await fetch(`${API_URL}/business-profile/documents/${doc.id}/preview`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename || doc.label;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_) { /* noop */ }
  };

  const groupedByCategory = (id) => documents.filter((d) => d.category === id);

  return (
    <section className="profile-section" data-testid="business-documents-section">
      <h2>Documentos del negocio</h2>
      <p>Sube y previsualiza los documentos oficiales de tu empresa (PDF, JPG, PNG o WEBP · Máx 15 MB).</p>
      {error && <p className="profile-error" data-testid="business-documents-error">{error}</p>}
      {notice && <p className="profile-notice" data-testid="business-documents-notice">{notice}</p>}

      <ul style={{ padding: 0, listStyle: "none", display: "grid", gap: 12 }}>
        {CATEGORIES.map((cat) => {
          const docs = groupedByCategory(cat.id);
          return (
            <li key={cat.id} className="business-doc-group" data-testid={`business-doc-group-${cat.id}`}>
              <header>
                <div>
                  <b>{cat.label}</b>
                  <small>{docs.length ? `${docs.length} archivo${docs.length === 1 ? "" : "s"}` : "Sin archivos aún"}</small>
                </div>
                <label className="business-doc-upload">
                  {uploading ? <Loader2 size={14} className="spin" /> : <Upload size={14} />} Subir
                  <input
                    type="file"
                    accept="application/pdf,image/jpeg,image/png,image/webp"
                    onChange={(e) => upload(e.target.files?.[0], cat.id)}
                    style={{ display: "none" }}
                    data-testid={`business-doc-upload-${cat.id}`}
                  />
                </label>
              </header>
              {docs.length > 0 && (
                <ul className="business-doc-list">
                  {docs.map((doc) => (
                    <li key={doc.id} data-testid={`business-doc-${doc.id}`}>
                      <FileText size={16} />
                      <span className="business-doc-info">
                        <b>{doc.label}</b>
                        <small>{doc.filename} · {bytes(doc.size)}</small>
                      </span>
                      <div className="business-doc-actions">
                        <button type="button" onClick={() => openPreview(doc)} title="Previsualizar" data-testid={`business-doc-preview-${doc.id}`}><Eye size={15} /></button>
                        <button type="button" onClick={() => rename(doc.id, doc.label)} title="Renombrar" data-testid={`business-doc-rename-${doc.id}`}><FileText size={15} /></button>
                        <button type="button" onClick={() => download(doc)} title="Descargar" data-testid={`business-doc-download-${doc.id}`}><Download size={15} /></button>
                        <button type="button" onClick={() => remove(doc.id)} title="Eliminar" data-testid={`business-doc-delete-${doc.id}`}><Trash2 size={15} /></button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
        <li style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#fff5f5", border: "1px dashed #fca5a5", borderRadius: 12, padding: "12px 16px" }}>
          <div>
            <b style={{ display: "block", color: "#b91c1c" }}>e.firma (.cer/.key) y CSD</b>
            <small style={{ fontWeight: 500, color: "#7f1d1d" }}>Bloqueado hasta activar el cifrado seguro (fase próxima).</small>
          </div>
          <span style={{ padding: "6px 12px", background: "#fecaca", color: "#7f1d1d", borderRadius: 999, font: "700 11px Nunito, sans-serif" }}>Próximamente</span>
        </li>
      </ul>

      {preview && (
        <div className="business-doc-preview" onClick={() => { URL.revokeObjectURL(preview.url); setPreview(null); }} data-testid="business-doc-preview-modal">
          <div className="business-doc-preview-inner" onClick={(e) => e.stopPropagation()}>
            <header>
              <b>{preview.label}</b>
              <button type="button" onClick={() => { URL.revokeObjectURL(preview.url); setPreview(null); }} data-testid="business-doc-preview-close"><X size={18} /></button>
            </header>
            {preview.content_type?.startsWith("image/") ? (
              <img src={preview.url} alt={preview.label} />
            ) : (
              <iframe title={preview.label} src={preview.url} />
            )}
          </div>
        </div>
      )}
    </section>
  );
}
